import { useEffect, useMemo } from 'react';
import { useAnalysisData } from '../../contexts/AnalysisDataContext';
import { AnalysisRange } from './useAnalysisRange';
import { Granularity } from '../../utils/analytics/types';
import { daysBetween } from '../../utils/dateUtils';
import {
  computeAdr,
  computeRevPas,
  leadTimeBuckets,
  medianLeadTime,
  occupiedSiteNights,
  revenueBySiteType,
  revenueInWindow,
  RevenueMode,
  revenueSeries,
} from '../../utils/analytics/revenueMetrics';
import { totalBlackoutSiteNights } from '../../utils/analytics/occupancyMetrics';
import { isRevenueStay } from '../../utils/analytics/types';
import { ComparisonMode, comparisonRange, makeDelta } from '../../utils/analytics/comparison';

export function useSalesAnalysis(
  range: AnalysisRange,
  granularity: Granularity,
  mode: RevenueMode,
  comparison: ComparisonMode = 'none'
) {
  const { stays, sites, siteTypes, blackouts, loading, error, ensureRange } = useAnalysisData();
  const prevRange = useMemo(() => comparisonRange(range, comparison), [range, comparison]);

  useEffect(() => {
    ensureRange(prevRange ? prevRange.from : range.from, range.to);
  }, [range.from, range.to, prevRange, ensureRange]);

  return useMemo(() => {
    const { from, to } = range;
    const siteIds = sites.map(s => s.id);

    const kpisFor = (f: string, t: string) => {
      const nights = daysBetween(f, t) + 1;
      const availableSiteNights =
        siteIds.length * nights - totalBlackoutSiteNights(blackouts, siteIds, f, t);
      return {
        revenue: revenueInWindow(stays, f, t, mode),
        bookings: stays.filter(isRevenueStay).filter(s => s.checkIn >= f && s.checkIn <= t).length,
        adr: computeAdr(stays, f, t),
        revpas: computeRevPas(stays, f, t, availableSiteNights),
        medianLead: medianLeadTime(stays, f, t),
      };
    };

    const current = kpisFor(from, to);
    const prior = prevRange ? kpisFor(prevRange.from, prevRange.to) : null;

    const typeIdBySiteId = new Map(sites.filter(s => s.type_id != null).map(s => [s.id, s.type_id!]));
    const typeNameById = new Map(siteTypes.map(t => [t.id, t.name]));

    return {
      loading,
      error,
      totalRevenue: current.revenue,
      bookings: current.bookings,
      adr: current.adr,
      revpas: current.revpas,
      medianLead: current.medianLead,
      occupiedNights: occupiedSiteNights(stays, from, to),
      deltas: prior
        ? {
            revenue: makeDelta(current.revenue, prior.revenue),
            bookings: makeDelta(current.bookings, prior.bookings),
            adr: makeDelta(current.adr, prior.adr),
            revpas: makeDelta(current.revpas, prior.revpas),
          }
        : null,
      series: revenueSeries(stays, from, to, granularity, mode),
      leadBuckets: leadTimeBuckets(stays, from, to),
      siteTypeRevenue: revenueBySiteType(stays, from, to, typeIdBySiteId, typeNameById),
    };
  }, [stays, sites, siteTypes, blackouts, range, prevRange, granularity, mode, loading, error]);
}
